/**
 * The dealer lookup / verifier each brand has, as the Dealer lists page shows
 * it. Same queries (and cache keys) and the same `mergeRows`, so the brand
 * directory cannot describe a brand's verifier differently from that page.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brandDealerDirectoryApi } from '../../services/brandDealerDirectoryApi';
import { dealerLocatorApi } from '../../services/dealerLocatorApi';
import { CombinedRow, dealerKindLabel, dealerRowLookup, mergeRows } from './dealerListRows';

export type DealerVerifierLookup = (brand: {
  id: string;
  slug?: string;
  name: string;
  aliases?: string[];
}) => CombinedRow | undefined;

export function useDealerVerifiers(): { lookup: DealerVerifierLookup | null; isLoading: boolean } {
  const directoryQuery = useQuery({
    queryKey: ['admin-brand-dealer-directory'],
    queryFn: () => brandDealerDirectoryApi.list(),
  });
  const locatorQuery = useQuery({
    queryKey: ['dealer-locators'],
    queryFn: () => dealerLocatorApi.list(),
  });

  const lookup = useMemo<DealerVerifierLookup | null>(() => {
    if (directoryQuery.isError || locatorQuery.isError) return () => undefined;
    if (!directoryQuery.data || !locatorQuery.data) return null;
    const rows = mergeRows(
      directoryQuery.data.sources || [],
      directoryQuery.data.imports || {},
      locatorQuery.data,
      { claims: directoryQuery.data.claims, stores: directoryQuery.data.stores },
      directoryQuery.data.letterOnlyBrands || [],
    );
    return dealerRowLookup(rows, directoryQuery.data.sources || []);
  }, [directoryQuery.data, locatorQuery.data, directoryQuery.isError, locatorQuery.isError]);

  return { lookup, isLoading: directoryQuery.isLoading || locatorQuery.isLoading };
}

/** One line for a brand's dealer check, e.g. "Authorized list · imported". */
export function dealerVerifierSummary(row: CombinedRow | undefined): { label: string; detail: string; ready: boolean } {
  if (!row) return { label: 'Proof only', detail: 'No published list or locator.', ready: false };
  const parts: string[] = [];
  if (row.kind === 'list' || row.kind === 'list_locator') {
    parts.push(row.imported ? `list imported (${row.imported.count} dealers)` : 'list not imported yet');
  }
  if (row.claimTime) parts.push('checked live at claim');
  else if (row.hasLocator) parts.push('locator checked by hand');
  if (row.kind === 'letter') parts.push('stores upload proof for review');
  if (row.kind === 'api_pending') parts.push('verification API not available yet');
  return {
    label: dealerKindLabel[row.kind],
    detail: parts.join(' · '),
    ready: Boolean(row.imported || row.claimTime),
  };
}
